import { useState } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { Loader2, AlertTriangle, Info, HeartPulse, Flag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { api, errMsg } from "../lib/api";

export const Page = ({ children, className = "", testId }) => (
  <main className={`mx-auto w-full max-w-6xl px-4 pb-28 pt-6 sm:px-6 lg:pb-12 ${className}`} data-testid={testId}>{children}</main>
);

export function ProgressRing({ value = 0, size = 120, stroke = 10, label, testId }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative inline-grid place-items-center" style={{ width: size, height: size }} data-testid={testId} role="img" aria-label={`${value}% ${label || "complete"}`}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#E5EFEA" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#2C4A3E" strokeWidth={stroke} fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - (c * Math.min(value, 100)) / 100} style={{ transition: "stroke-dashoffset .8s ease" }} />
      </svg>
      <div className="absolute text-center">
        <div className="font-display text-2xl font-bold text-ckb-ink">{value}%</div>
        {label && <div className="text-[11px] text-ckb-muted">{label}</div>}
      </div>
    </div>
  );
}

export const Loading = ({ label }) => {
  const { t } = useI18n();
  return <div className="flex items-center justify-center gap-2 py-16 text-ckb-muted" role="status" data-testid="loading-state"><Loader2 className="h-5 w-5 animate-spin" />{label || t("loading")}</div>;
};

export const ErrorState = ({ message, onRetry }) => {
  const { t } = useI18n();
  return (
    <div className="ckb-card flex flex-col items-center gap-3 p-8 text-center" role="alert" data-testid="error-state">
      <AlertTriangle className="h-6 w-6 text-ckb-ochre" />
      <p className="text-ckb-ink2">{message || t("error_generic")}</p>
      {onRetry && <button className="ckb-btn-ghost" onClick={onRetry} data-testid="retry-button">{t("retry")}</button>}
    </div>
  );
};

export const EmptyState = ({ title, text, action, testId = "empty-state" }) => (
  <div className="ckb-card flex flex-col items-center gap-2 p-10 text-center" data-testid={testId}>
    <div className="font-display text-lg font-semibold text-ckb-ink">{title}</div>
    {text && <p className="max-w-sm text-sm text-ckb-ink2">{text}</p>}
    {action}
  </div>
);

export const Disclaimer = ({ children, testId = "contextual-disclaimer" }) => (
  <div className="flex gap-3 rounded-2xl border border-ckb-line bg-ckb-sand2/70 p-4 text-sm text-ckb-ink2" data-testid={testId}>
    <Info className="mt-0.5 h-4 w-4 shrink-0 text-ckb-sage" aria-hidden />
    <div>{children || <>General wellness education only — not a diagnosis or treatment. <Link to="/medical-disclaimer" className="underline">Medical disclaimer</Link></>}</div>
  </div>
);

export const RedFlagNotice = ({ crisis, testId = "red-flag-notice" }) => (
  <div className="flex gap-3 rounded-2xl border border-ckb-ochre/40 bg-[#FBEFEA] p-4 text-sm text-ckb-ink" role="alert" data-testid={testId}>
    <HeartPulse className="mt-0.5 h-5 w-5 shrink-0 text-ckb-ochre" aria-hidden />
    <div>
      <div className="font-semibold">This may need professional medical attention.</div>
      <p className="mt-1 text-ckb-ink2">{crisis || "Please consider seeing a doctor soon. If symptoms are severe or sudden, seek urgent in-person care (India emergency: 112)."}</p>
    </div>
  </div>
);

// Renders trusted article/lesson text safely without HTML injection: "## " headings, "- " lists, paragraphs.
// Renders **bold** and *italic* as React elements (no raw HTML).
function Inline({ text }) {
  return text.split(/(\*\*[^*]+\*\*|\*[^*\s][^*]*\*)/g).map((s, i) => (
    s.startsWith("**") && s.endsWith("**") && s.length > 4 ? <strong key={i}>{s.slice(2, -2)}</strong>
      : s.startsWith("*") && s.endsWith("*") && s.length > 2 ? <em key={i}>{s.slice(1, -1)}</em> : s
  ));
}

export function SafeContent({ text = "" }) {
  const blocks = text.split(/\n\n+/);
  const li = (l, j) => <li key={j}><Inline text={l.slice(2)} /></li>;
  return (
    <div className="ckb-prose">
      {blocks.map((b, i) => {
        const lines = b.split("\n");
        if (lines.every((l) => l.startsWith("- "))) return <ul key={i}>{lines.map(li)}</ul>;
        if (b.startsWith("## ")) {
          const [h, ...rest] = lines;
          return <div key={i}><h2><Inline text={h.slice(3)} /></h2>{rest.length > 0 && (rest.every((l) => l.startsWith("- ")) ? <ul>{rest.map(li)}</ul> : <p><Inline text={rest.join(" ")} /></p>)}</div>;
        }
        return <p key={i} className="whitespace-pre-line"><Inline text={b} /></p>;
      })}
    </div>
  );
}

export function ProtectedRoute({ children, admin = false }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (admin && user.role !== "ADMIN") return <Navigate to="/dashboard" replace />;
  return children;
}

const REASONS = [["medical_misinformation", "Medical misinformation"], ["harassment", "Harassment"], ["spam", "Spam"], ["personal_information", "Personal information"], ["dangerous_advice", "Dangerous advice"], ["inappropriate", "Inappropriate content"], ["other", "Other"]];

export function ReportDialog({ open, onOpenChange, contentType, contentId }) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/community/report", { content_type: contentType, content_id: contentId, reason, details });
      toast.success(data.message);
      onOpenChange(false);
      setReason(""); setDetails("");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="report-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Flag className="h-4 w-4" /> Report content</DialogTitle><DialogDescription>Reports are private and reviewed by our team.</DialogDescription></DialogHeader>
        <RadioGroup value={reason} onValueChange={setReason} className="space-y-1">
          {REASONS.map(([v, l]) => (
            <div key={v} className="flex items-center gap-3"><RadioGroupItem value={v} id={`r-${v}`} data-testid={`report-reason-${v}`} /><Label htmlFor={`r-${v}`}>{l}</Label></div>
          ))}
        </RadioGroup>
        <Textarea placeholder="Optional details (don't include personal info)" maxLength={500} value={details} onChange={(e) => setDetails(e.target.value)} data-testid="report-details-input" />
        <button className="ckb-btn-primary" disabled={!reason || busy} onClick={submit} data-testid="report-submit-button">Submit report</button>
      </DialogContent>
    </Dialog>
  );
}

export const timeAgo = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.round(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};
